import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/site/Logo";

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const inputCls = (err) =>
  `w-full rounded-lg border bg-[#0B0F18] px-4 py-3 text-sm text-white outline-none transition-colors duration-200 focus:border-cyan-400/70 focus:ring-2 focus:ring-cyan-400/20 ${err ? "border-red-500/70" : "border-white/10"}`;

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErr, setFieldErr] = useState({});
  const [error, setError] = useState(location.state?.error || "");
  const [busy, setBusy] = useState(false);

  if (user && user.role === "admin") return <Navigate to="/admin/leads" replace />;
  if (user && user.role === "client") return <Navigate to="/portal" replace />;

  const submit = async (e) => {
    e.preventDefault();
    const fe = {};
    if (!email.trim()) fe.email = "Please enter your email";
    if (!password) fe.password = "Please enter your password";
    setFieldErr(fe);
    setError("");
    if (Object.keys(fe).length) return;
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      navigate(u.role === "admin" ? "/admin/leads" : "/portal", { replace: true });
    } catch (err) {
      const s = err.response?.status;
      setError(s === 401 ? "Invalid email or password" : formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="login-page" className="noise relative flex min-h-screen items-center justify-center overflow-hidden bg-[#07090E] px-4 py-12">
      <div className="bg-grid grid-fade absolute inset-0" />
      <div className="glow-orb pulse-glow absolute left-1/2 top-1/4 h-96 w-96 -translate-x-1/2" />
      <div className="rise relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0D111A]/80 p-6 backdrop-blur-xl sm:p-10">
        <Logo />
        <h1 className="mt-8 text-2xl font-bold tracking-tight text-white sm:text-3xl">Sign in</h1>
        <p className="mt-2 text-sm text-slate-400">Access your Beall Digital Group dashboard.</p>
        <button type="button" onClick={google} data-testid="login-google-button"
          className="mt-8 flex w-full items-center justify-center gap-3 rounded-full border border-white/15 bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5">
          <GoogleIcon /> Sign in with Google
        </button>
        <div className="my-6 flex items-center gap-3 text-xs text-slate-500">
          <span className="h-px flex-1 bg-white/10" /> or with email <span className="h-px flex-1 bg-white/10" />
        </div>
        <form onSubmit={submit} noValidate className="space-y-4" data-testid="login-form">
          <div>
            <label htmlFor="login-email" className="mb-2 block text-sm text-slate-300">Email</label>
            <input id="login-email" type="email" autoComplete="email" data-testid="login-input-email" value={email}
              onChange={(e) => setEmail(e.target.value)} className={inputCls(fieldErr.email)} />
            {fieldErr.email && <p data-testid="login-email-error" className="mt-1.5 text-xs text-red-400">{fieldErr.email}</p>}
          </div>
          <div>
            <label htmlFor="login-password" className="mb-2 block text-sm text-slate-300">Password</label>
            <input id="login-password" type="password" autoComplete="current-password" data-testid="login-input-password" value={password}
              onChange={(e) => setPassword(e.target.value)} className={inputCls(fieldErr.password)} />
            {fieldErr.password && <p data-testid="login-password-error" className="mt-1.5 text-xs text-red-400">{fieldErr.password}</p>}
          </div>
          {error && <p data-testid="login-error-message" role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-300">{error}</p>}
          <button type="submit" disabled={busy} data-testid="login-submit-button"
            className="btn-glow flex w-full items-center justify-center gap-2 rounded-full bg-cyan-400 px-5 py-3 text-sm font-semibold text-[#07090E] hover:bg-cyan-300 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
          </button>
        </form>
        <a href="/" data-testid="login-back-home" className="mt-6 block text-center text-sm text-slate-500 transition-colors hover:text-cyan-300">← Back to website</a>
      </div>
    </div>
  );
}
